/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";

import ChatParticipants from "./ChatParticipants";
import ChatFeed from "./ChatFeed";
import ChatOverlay from "./ChatOverlay";
import PinnedMessageBanner from "./PinnedMessageBanner";
import DirectChatBlockModal from "./DirectChatBlockModal";
import DirectChatManageModal from "./DirectChatManageModal";
import StaffEventModerationCard from "./StaffEventModerationCard";
import AutoFitHeaderTitle from "./AutoFitHeaderTitle";
import ChatNotificationBellIcon from "./ChatNotificationBellIcon";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { BADGES } from "../../constants/badges";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { formatChatHeaderEventDate } from "../../utils/chatHeaderDate";
import { formatCompactNumber } from "../../utils/compactNumber";
import ValidationPendingNotice from "../events/ValidationPendingNotice";

const CHAT_PANEL_COLOR = "#0D1428";
const CHAT_PRIMARY_BLUE = "#3E6FFB";
const DIRECT_PROFILE_CAPSULE_SHAPE_CLASS =
  "rounded-bl-[67px] rounded-br-[24px] rounded-tl-[67px] rounded-tr-[24px]";
const BALANCE_ICON_CLASS = "h-[18px] w-[18px] shrink-0 object-contain";
const CHAT_BODY_FADE_VARIANTS = {
  initial: { opacity: 0, filter: "blur(5px)" },
  animate: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    filter: "blur(5px)",
    transition: { duration: 0.14, ease: [0.22, 1, 0.36, 1] },
  },
};
const CHAT_KEYBOARD_FOREGROUND_STYLE = {
  transform:
    "translate3d(0, calc(0px - var(--vg-chat-keyboard-inset, 0px) + var(--vg-chat-keyboard-resting-offset, 0px)), 0)",
  transition: "transform 250ms cubic-bezier(0.32, 0.72, 0, 1)",
  willChange: "transform",
};
const CHAT_STATIONARY_FOREGROUND_STYLE = {
  transform: "translate3d(0, 0, 0)",
};
const CHAT_TOP_FADE_STYLE = {
  background:
    "linear-gradient(180deg, #0D1428 0%, rgba(13,20,40,0.96) 26%, rgba(13,20,40,0.7) 58%, rgba(13,20,40,0) 100%)",
  transform: "translateZ(0)",
  WebkitTransform: "translateZ(0)",
};

function resolveEventHeaderImageSrc(event) {
  const iconImage = String(event?.icon_image_url || "").trim();
  if (iconImage) return iconImage;

  const badgeLabel = String(event?.badge_label || "").trim();
  return BADGES.find((badge) => badge.id === badgeLabel)?.src || null;
}

function resolveTypingAvatarSrc(user) {
  return resolveUserAvatarSrc(user);
}

function resolveDirectAvatarSrc(peer) {
  return resolveUserAvatarSrc(peer);
}

function DirectProfileCapsule({ peer, directCode, onOpen, t, notificationsMuted = false, notificationsMutedAriaLabel = "" }) {
  const isDeletedPeer = isDeletedAccountUser(peer);
  const code = isDeletedPeer ? DELETED_USER_LABEL : String(peer?.code || directCode || "---").trim().toUpperCase();
  const username = isDeletedPeer ? "" : formatUserHandle(peer?.username);
  const avatarSrc = resolveDirectAvatarSrc(peer);
  const diamonds = peer?.diamonds ?? peer?.diamond_balance ?? 0;
  const coins = peer?.coins ?? peer?.coin_balance ?? 0;
  const level = peer?.level ?? peer?.storage_level ?? 0;
  const isPremium = !isDeletedPeer && Boolean(peer?.has_premium_subscription || peer?.is_premium || peer?.isPremium);

  return (
    <button
      type="button"
      onClick={() => {
        if (isDeletedPeer) return;
        onOpen?.();
      }}
      className={`absolute left-[30px] right-[30px] top-[calc(11px+var(--vg-safe-area-top))] flex h-20 items-center justify-between overflow-hidden ${DIRECT_PROFILE_CAPSULE_SHAPE_CLASS} bg-white px-[10px] py-[10px] text-left shadow-[0_14px_28px_rgba(0,0,0,0.16)] transition active:scale-[0.995] ${isDeletedPeer ? "cursor-default" : ""}`}
      aria-label={t?.("chats.direct_chat.open_profile_aria") || username || code}
    >
      <div className="flex min-w-0 flex-1 items-center gap-[10px]">
        <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#D9D9D9]">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={username || code}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : isDeletedPeer ? (
            <span className="text-[13px] font-black leading-none text-black/45">DU</span>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col">
          {isDeletedPeer ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="max-w-[132px] truncate text-[18px] font-black leading-[22px] text-black">
                {DELETED_USER_LABEL}
              </p>
              {notificationsMuted ? (
                <span
                  className="inline-flex shrink-0 text-black/55"
                  aria-label={notificationsMutedAriaLabel}
                  title={notificationsMutedAriaLabel}
                >
                  <ChatNotificationBellIcon muted size={18} />
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex min-w-0 items-center gap-1.5">
                <PremiumCodeLabel
                  code={code}
                  isPremium={isPremium}
                  outlineColorClassName="border-black"
                  className="max-w-[112px] truncate text-[20px] font-black leading-[22px] text-black"
                  as="p"
                />
                {notificationsMuted ? (
                  <span
                    className="inline-flex shrink-0 text-black/55"
                    aria-label={notificationsMutedAriaLabel}
                    title={notificationsMutedAriaLabel}
                  >
                    <ChatNotificationBellIcon muted size={18} />
                  </span>
                ) : null}
              </div>
              <p className="max-w-[132px] truncate text-[14px] font-bold leading-[18px] text-black">
                {username}
              </p>
            </>
          )}
        </div>
      </div>

      {!isDeletedPeer ? (
        <div className="flex h-[55px] w-[142px] shrink-0 items-center justify-end overflow-visible">
          <div className="flex shrink-0 items-center gap-2">
            <div className="grid grid-cols-[58px_18px] items-center gap-x-1 gap-y-[6px]">
              <span className="whitespace-nowrap text-right text-[14px] font-semibold tabular-nums leading-[17px] text-black">
                {formatCompactNumber(diamonds)}
              </span>
              <img src="/assets/vault/diamand_icon.svg" alt="" className={BALANCE_ICON_CLASS} />
              <span className="whitespace-nowrap text-right text-[14px] font-semibold tabular-nums leading-[17px] text-black">
                {formatCompactNumber(coins)}
              </span>
              <img src="/assets/vault/coin_icon.svg" alt="" className={BALANCE_ICON_CLASS} />
            </div>
            <div className="flex h-[48px] w-[48px] flex-col items-center justify-center rounded-lg border border-black/20">
              <span className="text-center text-[15px] font-bold leading-[17px] text-black">
                {formatCompactNumber(level)}
              </span>
              <span className="text-center text-[10px] font-semibold leading-[11px] text-black">
                level
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </button>
  );
}

// ================================
// MOBILE VIEWS
// ================================
// This file will contain TWO mobile render-views:
// 1) Event chat mobile view
// 2) Direct (private) chat mobile view
//
// Each container (Chat.jsx / DirectChat.jsx) will import the view it needs.

// --------------------------------
// 1) EVENT CHAT — MOBILE VIEW
// --------------------------------
export function EventChatMobileView({
  // refs
  headerRef,
  feedRef,

  // i18n
  t,
  language,

  // header actions
  handleBackClick,
  openEventFromChatHeader,
  openClubFromChatHeader,
  headerOpensEvent = false,
  headerTitle,
  isGroupChat = false,
  clubChat = null,
  groupDisplayTitle = "",
  groupParticipantsLabel = "",
  headerSecondaryActionLabel,
  headerSecondaryActionDisabled,
  onHeaderSecondaryAction,
  headerActionIconSrc = "",
  headerActionLabel = "",
  headerActionDisabled = false,
  onHeaderAction = null,
  headerActionActive = false,
  headerAuxActionLabel = "",
  headerAuxActionDisabled = false,
  onHeaderAuxAction = null,
  headerAuxActionActive = false,
  notificationsMuted = false,
  notificationsMutedAriaLabel = "",
  viewportStyle = null,

  eventName,
  eventDateTime,
  eventType,
  eventForCard = null,
  staffModerationAssessment = null,
  onSendHostRules = null,
  sendingHostRules = false,
  onSendCommercialNotice = null,
  sendingCommercialNotice = false,
  onRejectWithTemplate = null,
  rejectingTemplateCode = "",
  showHostValidationPending = false,
  validationPendingIsConcept = false,
  onOpenHostRules = null,
  // participants capsule
  participantsCount,
  participantsLabelOverride = "",
  participantsDisabled = false,
  setShowParticipants,
  showParticipants,
  participantsActionLabel = "",
  participantsActionDisabled = false,
  onParticipantsActionClick = null,
  closeCtxMenu,

  // body/feed
  typingUsers = [],
  onFeedScroll,
  feedBottomPadPx,
  ctxMenu,
  imageViewer,
  items,
  pinnedMessages = [],
  pinnedMessage: legacyPinnedMessage,
  openCtxMenu,
  flashId,
  jumpToMessage,
  avatarByCode,
  openImageViewer,
  openProfileByCode,
  mentionContext,
  currentUserId,
  allowSystemMessageActions = false,
  messageSelectionMode,
  selectedMessageId,
  canSelectMessage,
  onSelectMessage,
  onOpenLocationMessage,
  onVotePoll,
  welcomeCrewPanel = null,

  // participants list
  eventId,
  participantsEndpoint,
  setParticipantsCount,
  onParticipantsLoaded,
  participantsReloadKey = 0,
  participantSelectionMode = false,
  selectedParticipantId = null,
  canSelectParticipant = null,
  onSelectParticipant = null,

  // overlay
  closeImageViewer,
  copyMessage,
  beginReply,
  beginEdit,
  deleteMessage,
  onTogglePin,
  onToggleReaction,
  canEdit,
  canDelete,
  getHeaderBottomPx,
  seenPanel,
  toggleSeenPanel,
}) {
  const visiblePinnedMessages = pinnedMessages.length ? pinnedMessages : legacyPinnedMessage?.id ? [legacyPinnedMessage] : [];
  const pinnedMessage = visiblePinnedMessages[visiblePinnedMessages.length - 1] || null;
  const topTypingUsers = Array.isArray(typingUsers) ? typingUsers.filter(Boolean) : [];
  const pinnedBannerTop = 8;
  const topTypingStatusText = topTypingUsers.length > 1 ? "are typing..." : "is typing...";
  const typingBelowPin = Boolean(pinnedMessage && !showParticipants);
  const topTypingIndicator = topTypingUsers.length ? (
    <div className={`absolute left-1/2 ${typingBelowPin ? "top-[68px]" : "top-[calc(145px+var(--vg-safe-area-top))]"} z-30 flex h-[30px] max-w-[250px] -translate-x-1/2 items-center rounded-[40px] bg-white/10 px-3 text-center`}>
      <div className="flex shrink-0 items-center pr-2">
        {topTypingUsers.slice(0, 3).map((user, index) => {
          const avatarSrc = resolveTypingAvatarSrc(user);
          const fallback = String(user?.code || user?.username || "?").slice(0, 1).toUpperCase();
          return (
            <span
              key={user?.id || `${fallback}-${index}`}
              className={`flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border border-[#0D1428] bg-[#5978BF] text-[9px] font-black leading-none text-white ${index ? "-ml-1.5" : ""}`}
            >
              {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : fallback}
            </span>
          );
        })}
        {topTypingUsers.length > 3 ? (
          <span className="-ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border border-[#0D1428] bg-[#5978BF] px-1 text-[9px] font-black leading-none text-white">
            +{topTypingUsers.length - 3}
          </span>
        ) : null}
      </div>
      <span className="truncate text-[14px] font-normal leading-[22px] text-white">{topTypingStatusText}</span>
    </div>
  ) : null;
  const eventHeaderTitle = String(
    headerTitle || eventName || clubChat?.name || groupDisplayTitle || t?.("chats.event_chat.title") || ""
  ).trim();
  const eventHeaderDateLabel = formatChatHeaderEventDate(eventDateTime, eventType, { language, t });
  const eventHeaderImageSrc = resolveEventHeaderImageSrc(eventForCard);
  const participantsLabel = participantsLabelOverride || (isGroupChat
    ? (groupParticipantsLabel || t?.("chats.event_chat.participants", { count: participantsCount }))
    : t?.("chats.event_chat.participants", { count: participantsCount }));
  const showParticipantsAction = Boolean(showParticipants && participantsActionLabel);
  const visibleParticipantsLabel = showParticipantsAction
    ? participantsActionLabel
    : participantsLabel;

  return (
    <div
      className="vg-chat-visual-viewport relative isolate flex flex-col h-full min-h-0 overflow-hidden"
      style={{ ...AUTH_GRADIENT_STYLE, ...(viewportStyle || {}) }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[calc(51px+var(--vg-safe-area-top))] z-0 rounded-t-[45px] shadow-[0_-5px_25px_rgba(0,0,0,0.25)]"
        style={{ backgroundColor: CHAT_PANEL_COLOR }}
        aria-hidden="true"
      />

      {/* ===== TOP HEADER AREA ===== */}
      <div
        ref={headerRef}
        className="relative z-20 h-[calc(138px+var(--vg-safe-area-top))] shrink-0 overflow-visible"
      >
        <div
          className="absolute inset-x-0 bottom-0 top-[calc(51px+var(--vg-safe-area-top))] rounded-t-[45px] shadow-[0_-5px_25px_rgba(0,0,0,0.25)]"
          style={{ backgroundColor: CHAT_PANEL_COLOR }}
          aria-hidden="true"
        />
        <button
          onClick={handleBackClick}
          className="absolute left-[26px] top-[calc(109px+var(--vg-safe-area-top))] flex h-6 w-6 items-center justify-center text-white transition active:scale-[0.98]"
          aria-label={t?.("chats.event_chat.back_aria")}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {onHeaderAuxAction ? (
          <button
            type="button"
            onClick={onHeaderAuxAction}
            disabled={headerAuxActionDisabled}
            className={`absolute right-[62px] top-[calc(109px+var(--vg-safe-area-top))] flex h-6 w-6 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45 ${
              headerAuxActionActive ? "bg-white/12" : ""
            }`}
            aria-label={headerAuxActionLabel}
          >
            <ChatNotificationBellIcon muted={notificationsMuted} size={24} />
          </button>
        ) : null}

        {onHeaderAction && headerActionIconSrc ? (
          <button
            type="button"
            onClick={onHeaderAction}
            disabled={headerActionDisabled}
            className={`absolute right-[26px] top-[calc(109px+var(--vg-safe-area-top))] flex h-6 w-6 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45 ${
              headerActionActive ? "bg-white/12" : ""
            }`}
            aria-label={headerActionLabel}
          >
            <img
              src={headerActionIconSrc}
              alt=""
              className={`h-6 w-6 object-contain ${
                String(headerActionIconSrc || "").includes("alert_square") ? "invert" : ""
              }`}
            />
          </button>
        ) : onHeaderSecondaryAction ? (
          <button
            type="button"
            onClick={onHeaderSecondaryAction}
            disabled={headerSecondaryActionDisabled}
            className="absolute right-[26px] top-[111px] flex h-8 min-w-8 items-center justify-center rounded-full bg-white/10 px-2 text-[10px] font-semibold text-white backdrop-blur-sm transition active:scale-[0.98] disabled:opacity-45"
          >
            {headerSecondaryActionLabel}
          </button>
        ) : null}

        <button
          type="button"
          onClick={headerOpensEvent ? openEventFromChatHeader : isGroupChat ? openClubFromChatHeader : openEventFromChatHeader}
          className={`absolute top-[calc(11px+var(--vg-safe-area-top))] flex h-20 bg-white shadow-[0_10px_22px_rgba(0,0,0,0.08)] transition active:scale-[0.995] ${
            isGroupChat
              ? "left-[30px] right-[30px] flex-col items-center justify-center rounded-[67px] px-6 text-center"
              : "left-[24px] right-[24px] items-center gap-[10px] rounded-[16px] px-[10px] text-left"
          }`}
          aria-label={headerOpensEvent || !isGroupChat ? t?.("chats.event_chat.open_event") : eventHeaderTitle}
        >
          {isGroupChat ? (
            <div className="flex max-w-full items-center justify-center gap-2">
              <AutoFitHeaderTitle
                text={eventHeaderTitle}
                className="text-center font-bold leading-[1.1] text-black"
                wrapperClassName="min-w-0 max-w-full"
              />
              {notificationsMuted ? (
                <span
                  className="inline-flex shrink-0 text-black/55"
                  aria-label={notificationsMutedAriaLabel}
                  title={notificationsMutedAriaLabel}
                >
                  <ChatNotificationBellIcon muted size={19} />
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[14px] bg-[#D9D9D9]">
                {eventHeaderImageSrc ? (
                  <img
                    src={eventHeaderImageSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span className="text-[22px] font-black leading-none text-black/45">
                    {eventHeaderTitle.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <AutoFitHeaderTitle
                    text={eventHeaderTitle.toUpperCase()}
                    className="text-left font-bold leading-[1.1] text-black"
                    wrapperClassName="min-w-0 max-w-full"
                    truncate
                  />
                  {notificationsMuted ? (
                    <span
                      className="inline-flex shrink-0 text-black/55"
                      aria-label={notificationsMutedAriaLabel}
                      title={notificationsMutedAriaLabel}
                    >
                      <ChatNotificationBellIcon muted size={18} />
                    </span>
                  ) : null}
                </div>
                <span className="mt-1 block max-w-full truncate text-[14px] font-medium leading-[18px] text-black">
                  {eventHeaderDateLabel}
                </span>
              </div>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            closeCtxMenu?.();
            if (showParticipantsAction) {
              onParticipantsActionClick?.();
              return;
            }
            setShowParticipants?.(true);
          }}
          disabled={participantsDisabled || (showParticipantsAction && participantsActionDisabled)}
          className={`absolute left-1/2 top-[calc(103px+var(--vg-safe-area-top))] flex h-[35px] w-[217px] -translate-x-1/2 items-center justify-center rounded-[67px] px-3 text-center transition active:scale-[0.99] disabled:opacity-60 ${
            showParticipantsAction
              ? "border border-white/35 bg-white/[0.08] shadow-[0_8px_20px_rgba(0,0,0,0.16)] backdrop-blur-[10px]"
              : ""
          }`}
          style={{
            maxWidth:
              onHeaderAuxAction && (onHeaderAction || onHeaderSecondaryAction)
                ? "calc(100% - 184px)"
                : "calc(100% - 128px)",
            ...(showParticipantsAction ? {} : { backgroundColor: CHAT_PRIMARY_BLUE }),
          }}
          aria-label={visibleParticipantsLabel}
        >
          <span className="truncate text-[14px] font-medium leading-[22px] text-white">
            {visibleParticipantsLabel}
          </span>
        </button>

        {!typingBelowPin ? topTypingIndicator : null}
      </div>

      {/* ===== BODY ===== */}
      <div className="relative z-0 flex flex-1 min-h-0 flex-col overflow-hidden">
        {!showParticipants && welcomeCrewPanel ? (
          <div className="relative z-20 shrink-0 px-4 pt-4">
            {welcomeCrewPanel}
          </div>
        ) : null}
        <div className="relative flex-1 min-h-0 overflow-hidden">
        {!showParticipants ? (
          <div
            className="pointer-events-none absolute inset-x-0 -top-3 z-[15] h-[92px]"
            style={CHAT_TOP_FADE_STYLE}
            data-chat-top-fade="header"
            aria-hidden="true"
          />
        ) : null}
        {!showParticipants ? (
          <PinnedMessageBanner messages={visiblePinnedMessages} onJump={jumpToMessage} t={t} topOffset={pinnedBannerTop} />
        ) : null}
        {typingBelowPin ? topTypingIndicator : null}
        <div
          className="absolute inset-0"
          style={CHAT_KEYBOARD_FOREGROUND_STYLE}
          data-chat-keyboard-foreground="true"
        >
          <AnimatePresence mode="wait" initial={false}>
          {!showParticipants ? (
            <motion.div
              key="messages"
              className="absolute inset-0 flex min-h-0 flex-col"
              variants={CHAT_BODY_FADE_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div
                ref={feedRef}
                onScroll={onFeedScroll}
                data-chat-message-feed="true"
                style={{
                  paddingBottom: `${feedBottomPadPx}px`,
                  boxSizing: "border-box",
                  ...(pinnedMessage ? { paddingTop: topTypingUsers.length ? 108 : 72 } : {}),
                }}
                className={`relative z-0 flex-1 min-h-0 px-4 ${welcomeCrewPanel && !pinnedMessage ? "pt-0" : "pt-20"} overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] ${
                  ctxMenu || imageViewer ? "pointer-events-none" : ""
                }`}
              >
                {showHostValidationPending ? (
                  <div className="mb-4">
                    <ValidationPendingNotice
                      compact
                      title={t?.(
                        validationPendingIsConcept
                          ? "chats.event_chat.validation_pending.title_concept"
                          : "chats.event_chat.validation_pending.title_event"
                      )}
                      body={t?.(
                        validationPendingIsConcept
                          ? "chats.event_chat.validation_pending.body_concept"
                          : "chats.event_chat.validation_pending.body_event"
                      )}
                    />
                  </div>
                ) : null}
                <StaffEventModerationCard
                  assessment={staffModerationAssessment}
                  onSendHostRules={onSendHostRules}
                  sendingHostRules={sendingHostRules}
                  onSendCommercialNotice={onSendCommercialNotice}
                  sendingCommercialNotice={sendingCommercialNotice}
                  onRejectWithTemplate={onRejectWithTemplate}
                  rejectingTemplateCode={rejectingTemplateCode}
                />
                <ChatFeed
                  items={items}
                  contextSelectedId={ctxMenu?.item?.id || null}
                  onRequestMenu={({ item, anchorEl, rowEl }) =>
                    openCtxMenu?.({ item, anchorEl, rowEl })
                  }
                  onToggleReaction={onToggleReaction}
                  flashId={flashId}
                  onJumpTo={jumpToMessage}
                  avatarByCode={avatarByCode}
                  onOpenImage={openImageViewer}
                  onOpenProfileByCode={openProfileByCode}
                  variant={isGroupChat ? "group" : "event"}
                  mentionContext={mentionContext}
                  currentUserId={currentUserId}
                  allowSystemMessageActions={allowSystemMessageActions}
                  selectionMode={messageSelectionMode}
                  selectedMessageId={selectedMessageId}
                  canSelectMessage={canSelectMessage}
                  onSelectMessage={onSelectMessage}
                  onOpenLocation={onOpenLocationMessage}
                  onVotePoll={onVotePoll}
                  onOpenHostRules={onOpenHostRules}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="participants"
              className="absolute inset-0 flex min-h-0 flex-col overflow-hidden"
              variants={CHAT_BODY_FADE_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ChatParticipants
                eventId={eventId}
                participantsEndpoint={participantsEndpoint}
                participantsCount={participantsCount}
                onClose={() => setShowParticipants?.(false)}
                onParticipantsCountChange={(nextCount) => {
                  if (typeof nextCount === "number" && !Number.isNaN(nextCount)) {
                    setParticipantsCount?.(nextCount);
                  }
                }}
                onParticipantsLoaded={onParticipantsLoaded}
                reloadKey={participantsReloadKey}
                selectionMode={participantSelectionMode}
                selectedParticipantId={selectedParticipantId}
                canSelectParticipant={canSelectParticipant}
                onSelectParticipant={onSelectParticipant}
              />
            </motion.div>
          )}
          </AnimatePresence>
        </div>
        </div>
      </div>

      {!showParticipants && (
        <ChatOverlay
          ctxMenu={ctxMenu}
          onCloseCtxMenu={closeCtxMenu}
          copyMessage={copyMessage}
          beginReply={beginReply}
          beginEdit={beginEdit}
          deleteMessage={deleteMessage}
          onTogglePin={onTogglePin}
          pinnedMessageIds={visiblePinnedMessages.map((pin) => pin.id)}
          onToggleReaction={onToggleReaction}
          canEdit={canEdit}
          canDelete={canDelete}
          getHeaderBottomPx={getHeaderBottomPx}
          imageViewer={imageViewer}
          onCloseImageViewer={closeImageViewer}
          t={t}
          // Seen-by side panel
          seenPanel={seenPanel}
          onToggleSeen={toggleSeenPanel}
          variant="event"
          restrictBackdropToParent={false}
        />
      )}
    </div>
  );
}

// --------------------------------
// 2) DIRECT CHAT — MOBILE VIEW (placeholder for next step)
// --------------------------------
export function DirectChatMobileView({
  // refs
  headerRef,
  feedRef,
  feedContentRef,

  // i18n
  t,

  // header actions
  handleBackClick,

  // peer card
  peer,
  directCode,
  openPeerVault,

  // report/block capsule
  isBlocked,
  handleReportClick,
  handleManageClick,
  canManageChat,
  directLastSeenLabel = "",
  typingUsers = [],
  viewportStyle = null,
  notificationsMuted = false,
  notificationsMutedAriaLabel = "",
  canToggleNotifications = false,
  notificationsActionLabel = "",
  notificationsActionDisabled = false,
  onNotificationsAction = null,

  // body/feed
  onFeedScroll,
  feedBottomPadPx,
  pinSparseFeed = false,
  ctxMenu,
  imageViewer,
  items,
  pinnedMessages = [],
  pinnedMessage: legacyPinnedMessage,
  openCtxMenu,
  flashId,
  jumpToMessage,
  openImageViewer,
  onOpenProfileByCode,
  mentionContext,
  preStartNotice,
  onOpenLocationMessage,
  onVotePoll,
  directInlineReportMessageId,
  onDirectInlineReportClick,
  directInlineReportLabel,
  directInlineReportAria,
  directInlineReportDisabled,
  currentUserId = null,
  allowSystemMessageActions = false,
  onDirectPhotoConsentAction,
  directPhotoConsentSubmitting = false,

  // overlay
  closeCtxMenu,
  copyMessage,
  beginReply,
  beginEdit,
  deleteMessage,
  onTogglePin,
  onToggleReaction,
  canEdit,
  canDelete,
  getHeaderBottomPx,
  closeImageViewer,
  seenPanel,
  toggleSeenPanel,

  // block/report modal
  blockModalOpen,
  reportModalMode,
  closeBlockModal,
  reportCategory,
  setReportCategory,
  reportComment,
  setReportComment,
  submitBlock,
  blockSubmitting,
  safetyNoticeText,
  manageModalOpen,
  closeManageModal,
  onDeleteChat,
  onDeleteAndBlockChat,
  manageSubmitting,
}) {
  const visiblePinnedMessages = pinnedMessages.length ? pinnedMessages : legacyPinnedMessage?.id ? [legacyPinnedMessage] : [];
  const pinnedMessage = visiblePinnedMessages[visiblePinnedMessages.length - 1] || null;
  const directTypingUser = Array.isArray(typingUsers) ? typingUsers.filter(Boolean)[0] : null;
  const pinnedBannerTop = 8;
  const directTypingAvatar = resolveTypingAvatarSrc(directTypingUser);
  const directTypingFallback = String(
    directTypingUser?.code || directTypingUser?.username || "?"
  ).slice(0, 1).toUpperCase();
  const directTypingIndicator = directTypingUser ? (
    <div className={`absolute left-1/2 ${pinnedMessage ? "top-[68px]" : "top-[calc(145px+var(--vg-safe-area-top))]"} z-30 flex h-[30px] max-w-[250px] -translate-x-1/2 items-center gap-2 rounded-[40px] bg-white/10 px-3 text-center`}>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#0D1428] bg-[#5978BF] text-[9px] font-black leading-none text-white">
        {directTypingAvatar ? <img src={directTypingAvatar} alt="" className="h-full w-full object-cover" /> : directTypingFallback}
      </span>
      <span className="truncate text-[13px] font-medium leading-[20px] text-white">
        {t?.("chats.direct_chat.typing", { defaultValue: "is typing..." })}
      </span>
    </div>
  ) : null;

  return (
    <div
      className="vg-chat-visual-viewport relative isolate flex flex-col h-full min-h-0 overflow-hidden"
      style={{ ...AUTH_GRADIENT_STYLE, ...(viewportStyle || {}) }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[calc(51px+var(--vg-safe-area-top))] z-0 rounded-t-[45px] shadow-[0_-5px_25px_rgba(0,0,0,0.25)]"
        style={{ backgroundColor: CHAT_PANEL_COLOR }}
        aria-hidden="true"
      />

      {/* ===== TOP HEADER AREA ===== */}
      <div
        ref={headerRef}
        className="relative z-20 h-[calc(138px+var(--vg-safe-area-top))] shrink-0 overflow-visible"
      >
        <div
          className="absolute inset-x-0 bottom-0 top-[calc(51px+var(--vg-safe-area-top))] rounded-t-[45px] shadow-[0_-5px_25px_rgba(0,0,0,0.25)]"
          style={{ backgroundColor: CHAT_PANEL_COLOR }}
          aria-hidden="true"
        />

        <DirectProfileCapsule
          peer={peer}
          directCode={directCode}
          onOpen={openPeerVault}
          t={t}
          notificationsMuted={notificationsMuted}
          notificationsMutedAriaLabel={notificationsMutedAriaLabel}
        />

        <div className="absolute left-[26px] top-[calc(109px+var(--vg-safe-area-top))] flex items-center gap-3">
          <button
            type="button"
            onClick={handleBackClick}
            className="flex h-6 w-6 items-center justify-center text-white transition active:scale-[0.98]"
            aria-label={t?.("chats.direct_chat.back_aria")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {canToggleNotifications ? (
            <button
              type="button"
              onClick={onNotificationsAction}
              disabled={notificationsActionDisabled}
              className="flex h-6 w-6 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
              aria-label={notificationsActionLabel}
            >
              <ChatNotificationBellIcon muted={notificationsMuted} size={23} />
            </button>
          ) : null}
        </div>

        <div className="absolute right-[26px] top-[calc(109px+var(--vg-safe-area-top))] flex items-center gap-3">
          <button
            type="button"
            onClick={handleManageClick}
            disabled={!canManageChat || manageSubmitting}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
            aria-label={t?.("chats.direct_chat.delete_aria")}
          >
            <img
              src="/assets/menu/trash.svg"
              alt=""
              className="h-6 w-6 object-contain"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </button>
          <button
            type="button"
            onClick={handleReportClick}
            disabled={isBlocked}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
            aria-label={t?.("chats.direct_chat.report_aria")}
          >
            <img
              src="/assets/menu/alert_square.svg"
              alt=""
              className="h-6 w-6 object-contain invert"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </button>
        </div>

        {directLastSeenLabel ? (
          <div className="absolute left-1/2 top-[calc(103px+var(--vg-safe-area-top))] z-30 -translate-x-1/2">
            <div
              className="flex h-[35px] items-center justify-center rounded-[67px] px-4 text-center"
              style={{
                backgroundColor: CHAT_PRIMARY_BLUE,
                width: "min(210px, calc(100vw - 220px))",
              }}
              aria-label={directLastSeenLabel}
            >
              <span className="truncate text-[14px] font-medium leading-[22px] text-white">
                {directLastSeenLabel}
              </span>
            </div>
          </div>
        ) : null}

        {!pinnedMessage ? directTypingIndicator : null}
      </div>

      {/* ===== BODY ===== */}
      <div className="relative z-0 flex-1 min-h-0 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 -top-3 z-[15] h-[92px]"
          style={CHAT_TOP_FADE_STYLE}
          data-chat-top-fade="header"
          aria-hidden="true"
        />
        <PinnedMessageBanner messages={visiblePinnedMessages} onJump={jumpToMessage} t={t} topOffset={pinnedBannerTop} />
        {pinnedMessage ? directTypingIndicator : null}
        <div
          className="absolute inset-0"
          style={
            pinSparseFeed
              ? CHAT_STATIONARY_FOREGROUND_STYLE
              : CHAT_KEYBOARD_FOREGROUND_STYLE
          }
          data-chat-keyboard-foreground="true"
          data-chat-sparse-feed={pinSparseFeed ? "true" : "false"}
        >
        <div
          ref={feedRef}
          onScroll={onFeedScroll}
          data-chat-message-feed="true"
          style={{
            paddingBottom: `${feedBottomPadPx}px`,
            boxSizing: "border-box",
            ...(pinnedMessage ? { paddingTop: directTypingUser ? 108 : 72 } : {}),
          }}
          className={`relative z-0 h-full min-h-0 px-4 ${
            safetyNoticeText ? "pt-[88px]" : pinnedMessage ? "pt-20" : pinSparseFeed ? "pt-8" : "pt-20"
          } overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] ${
            ctxMenu || imageViewer ? "pointer-events-none" : ""
          }`}
        >
          <div ref={feedContentRef} data-chat-feed-content="true">
            {safetyNoticeText ? (
              <div className="mb-4 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-center text-[12px] font-semibold leading-[18px] text-white/90">
                {safetyNoticeText}
              </div>
            ) : null}
            <ChatFeed
              items={items}
              contextSelectedId={ctxMenu?.item?.id || null}
              onRequestMenu={({ item, anchorEl, rowEl }) =>
                openCtxMenu?.({ item, anchorEl, rowEl })
              }
              onToggleReaction={onToggleReaction}
              flashId={flashId}
              onJumpTo={jumpToMessage}
              avatarByCode={{}}
              onOpenImage={openImageViewer}
              onOpenProfileByCode={onOpenProfileByCode}
              hideSenderBadge={true}
              variant="direct"
              currentUserId={currentUserId}
              allowSystemMessageActions={allowSystemMessageActions}
              mentionContext={mentionContext}
              onOpenLocation={onOpenLocationMessage}
              onVotePoll={onVotePoll}
              directInlineReportMessageId={directInlineReportMessageId}
              onDirectInlineReportClick={onDirectInlineReportClick}
              directInlineReportLabel={directInlineReportLabel}
              directInlineReportAria={directInlineReportAria}
              directInlineReportDisabled={directInlineReportDisabled}
              onDirectPhotoConsentAction={onDirectPhotoConsentAction}
              directPhotoConsentSubmitting={directPhotoConsentSubmitting}
            />
          </div>
        </div>
          {preStartNotice ? (
            <div className="absolute inset-0 flex items-center justify-center px-8 pointer-events-none">
              <div className="w-full max-w-[340px] rounded-2xl border border-white/35 bg-black/25 backdrop-blur-sm text-white text-center mb-20 px-2 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.28)]">
              {preStartNotice?.isFree ? (
                <div className="text-[13px] font-semibold">
                  {t?.("chats.direct_chat.start_notice_free")}
                </div>
              ) : (
                <>
                  <div className="text-[14px] font-semibold leading-5 flex items-center justify-center gap-1">
                    <span>{t?.("chats.direct_chat.start_notice_charge_prefix")}</span>
                    <span className="inline-flex items-center gap-[2px]">
                      <span>{formatCompactNumber(preStartNotice.cost)}</span>
                      <img src="/assets/vault/coin_icon.svg" alt="" className="w-3.5 h-3.5" />
                    </span>
                  </div>
                  <div className="text-[12px] text-white/85 mt-1.5 leading-5">
                    {t?.("chats.direct_chat.start_notice_unlimited")}
                  </div>
                  <div className="text-[12px] text-white/85 mt-1 flex items-center justify-center gap-1">
                    <span>{t?.("chats.direct_chat.start_notice_balance")}</span>
                    <span className="inline-flex items-center gap-[2px]">
                      <span>{formatCompactNumber(preStartNotice.balance)}</span>
                      <img src="/assets/vault/coin_icon.svg" alt="" className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </>
              )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ChatOverlay
        ctxMenu={ctxMenu}
        onCloseCtxMenu={closeCtxMenu}
        copyMessage={copyMessage}
        beginReply={beginReply}
        beginEdit={beginEdit}
        deleteMessage={deleteMessage}
        onTogglePin={onTogglePin}
        pinnedMessageIds={visiblePinnedMessages.map((pin) => pin.id)}
        onToggleReaction={onToggleReaction}
        canEdit={canEdit}
        canDelete={canDelete}
        getHeaderBottomPx={getHeaderBottomPx}
        imageViewer={imageViewer}
        onCloseImageViewer={closeImageViewer}
        t={t}
        seenPanel={seenPanel}
        onToggleSeen={toggleSeenPanel}
        variant="direct"
        restrictBackdropToParent={false}
      />

      {/* Block/report modal (DM), styled similar to kick modal in ChatParticipants */}
      <DirectChatBlockModal
        open={!!blockModalOpen}
        mode={reportModalMode}
        onClose={closeBlockModal}
        category={reportCategory}
        onCategoryChange={setReportCategory}
        comment={reportComment}
        onCommentChange={setReportComment}
        onSubmit={submitBlock}
        submitting={!!blockSubmitting}
        t={t}
      />

      <DirectChatManageModal
        open={!!manageModalOpen}
        onClose={closeManageModal}
        onDelete={onDeleteChat}
        onDeleteAndBlock={onDeleteAndBlockChat}
        submitting={!!manageSubmitting}
        t={t}
      />
    </div>
  );
}

// Keep a default export so importing without braces is still possible.
// For now, default to the event chat view (containers should prefer named exports).
export default EventChatMobileView;
